package org.example.project.screens_cus

import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.example.project.manager.LumaticaApiManager
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.utils.All

private val urlaubJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun apiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

typealias Urlaubsanspruch = All
typealias Abwesenheitsbedarf = All
typealias ZeitkontoJournalEintrag = All

object UrlaubsApi {
    private fun firmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    private fun triggerUrlaubRefresh(userId: String = "") {
        val userPart = userId.trim().ifBlank { "~" }
        GlobalManager.markJsonChanged("urlaub", "user/$userPart/abwesenheit/urlaub.json")
    }

    suspend fun loadUsers(): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/users/${apiPart(firmenId())}")
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadAll(): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/list/${apiPart(firmenId())}")
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadYear(year: Int): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/list-year/${apiPart(firmenId())}/${apiPart(year.toString())}")
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadMonth(year: Int, month: Int): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/list-month/${apiPart(firmenId())}/${apiPart(year.toString())}/${apiPart(month.toString())}"
            )
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadUser(userId: String): List<All> {
        if (userId.isBlank()) return emptyList()
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/user/${apiPart(firmenId())}/${apiPart(userId)}")
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadUserYear(userId: String, year: Int): List<All> {
        if (userId.isBlank()) return emptyList()
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/user-year/${apiPart(firmenId())}/${apiPart(userId)}/${apiPart(year.toString())}"
            )
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadMyYear(year: Int): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/my-urlaub-year/${apiPart(year.toString())}")
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadUserMonth(userId: String, year: Int, month: Int): List<All> {
        if (userId.isBlank()) return emptyList()
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/user-month/${apiPart(firmenId())}/${apiPart(userId)}/" +
                    "${apiPart(year.toString())}/${apiPart(month.toString())}"
            )
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadAllowance(userId: String, year: Int): Urlaubsanspruch {
        if (userId.isBlank()) return Urlaubsanspruch(jahr = year)
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/anspruch/${apiPart(firmenId())}/${apiPart(userId)}/${apiPart(year.toString())}"
            )
        }.getOrDefault("")
        if (raw.isBlank()) return Urlaubsanspruch(userId = userId, jahr = year)
        return runCatching { urlaubJson.decodeFromString<Urlaubsanspruch>(raw) }
            .getOrDefault(Urlaubsanspruch(userId = userId, jahr = year))
    }

    suspend fun loadMyAllowance(year: Int): Urlaubsanspruch {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/my-anspruch/${apiPart(year.toString())}")
        }.getOrDefault("")
        if (raw.isBlank()) return Urlaubsanspruch(jahr = year)
        return runCatching { urlaubJson.decodeFromString<Urlaubsanspruch>(raw) }
            .getOrDefault(Urlaubsanspruch(jahr = year))
    }

    suspend fun loadAllowances(year: Int): List<Urlaubsanspruch> {
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/ansprueche/${apiPart(firmenId())}/${apiPart(year.toString())}"
            )
        }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<Urlaubsanspruch>>(raw) }
            .getOrDefault(emptyList())
    }

    suspend fun loadAbsenceRequirement(
        userId: String,
        datumVon: String,
        datumBis: String,
        abwesenheitsart: String
    ): Abwesenheitsbedarf {
        if (userId.isBlank() || datumVon.isBlank() || datumBis.isBlank()) {
            return Abwesenheitsbedarf(
                userId = userId,
                datumVon = datumVon,
                datumBis = datumBis,
                abwesenheitsart = abwesenheitsart,
                error = "ungueltige_anfrage"
            )
        }
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/bedarf/${apiPart(firmenId())}/${apiPart(userId)}/" +
                    "${apiPart(datumVon)}/${apiPart(datumBis)}/${apiPart(abwesenheitsart)}"
            )
        }.getOrDefault("")
        if (raw.isBlank()) {
            return Abwesenheitsbedarf(
                userId = userId,
                datumVon = datumVon,
                datumBis = datumBis,
                abwesenheitsart = abwesenheitsart,
                error = "bedarf_leer"
            )
        }
        return runCatching {
            val parsed = urlaubJson.parseToJsonElement(raw)
            val obj = parsed as? JsonObject
            if (obj != null && "zeitkonto" !in obj && obj["error"] != null) {
                Abwesenheitsbedarf(
                    userId = userId,
                    datumVon = datumVon,
                    datumBis = datumBis,
                    abwesenheitsart = abwesenheitsart,
                    error = obj["error"]?.jsonPrimitive?.content.orEmpty().ifBlank { "bedarf_fehler" }
                )
            } else {
                urlaubJson.decodeFromString<Abwesenheitsbedarf>(raw)
            }
        }.getOrElse {
            Abwesenheitsbedarf(
                userId = userId,
                datumVon = datumVon,
                datumBis = datumBis,
                abwesenheitsart = abwesenheitsart,
                error = "bedarf_decode_fehler"
            )
        }
    }

    suspend fun loadMyAbsenceRequirement(
        datumVon: String,
        datumBis: String,
        abwesenheitsart: String
    ): Abwesenheitsbedarf {
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/my-bedarf/${apiPart(datumVon)}/${apiPart(datumBis)}/${apiPart(abwesenheitsart)}"
            )
        }.getOrDefault("")
        if (raw.isBlank()) return Abwesenheitsbedarf(
            datumVon = datumVon,
            datumBis = datumBis,
            abwesenheitsart = abwesenheitsart,
            error = "bedarf_leer"
        )
        return runCatching { urlaubJson.decodeFromString<Abwesenheitsbedarf>(raw) }
            .getOrDefault(Abwesenheitsbedarf(
                datumVon = datumVon,
                datumBis = datumBis,
                abwesenheitsart = abwesenheitsart,
                error = "bedarf_decode_fehler"
            ))
    }

    suspend fun loadTimeAccountJournal(userId: String): List<ZeitkontoJournalEintrag> {
        if (userId.isBlank()) return emptyList()
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/zeitkonto-journal/${apiPart(firmenId())}/${apiPart(userId)}"
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { urlaubJson.decodeFromString<List<ZeitkontoJournalEintrag>>(raw) }
            .getOrDefault(emptyList())
    }

    suspend fun searchBookingJournalOverview(
        year: Int,
        filters: Map<String, String>
    ): List<All> {
        val raw = loadWithTabularFilters(
            "personalkontingent/buchungsjournal-overview-tabular",
            filters,
            mapOf("year" to year.toString())
        )
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun searchTimeAccountJournal(
        userId: String,
        filters: Map<String, String>
    ): List<ZeitkontoJournalEintrag> {
        if (userId.isBlank()) return emptyList()
        val raw = loadWithTabularFilters(
            "personalkontingent/buchungsjournal-detail-tabular",
            filters,
            mapOf("userId" to userId)
        )
        return runCatching { urlaubJson.decodeFromString<List<ZeitkontoJournalEintrag>>(raw) }
            .getOrDefault(emptyList())
    }

    suspend fun searchVacationOverview(
        year: Int,
        month: Int,
        filters: Map<String, String>
    ): List<All> {
        val raw = loadWithTabularFilters(
            "personalkontingent/urlaubsuebersicht-tabular",
            filters,
            mapOf("year" to year.toString(), "month" to month.toString())
        )
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun searchAllVacationOverview(filters: Map<String, String>): List<All> {
        val raw = loadWithTabularFilters(
            "personalkontingent/urlaubsuebersicht-tabular",
            filters
        )
        return runCatching { urlaubJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun saveAllowance(
        userId: String,
        year: Int,
        jahresurlaub: Double,
        resturlaub: Double,
        arbeitszeitTaeglich: Double,
        arbeitszeitMonatlich: Double,
        zeitkonto: Double,
        zeitreserve: Double
    ): Urlaubsanspruch? {
        if (userId.isBlank()) return null
        val body = buildJsonObject {
            put("userId", JsonPrimitive(userId))
            put("jahr", JsonPrimitive(year))
            put("jahresurlaub", JsonPrimitive(jahresurlaub))
            put("resturlaub", JsonPrimitive(resturlaub))
            put("arbeitszeitTaeglich", JsonPrimitive(arbeitszeitTaeglich))
            put("arbeitszeitMonatlich", JsonPrimitive(arbeitszeitMonatlich))
            put("zeitkonto", JsonPrimitive(zeitkonto))
            put("zeitreserve", JsonPrimitive(zeitreserve))
        }.toString()
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/kontingent-save-json/${apiPart(firmenId())}/${apiPart(body)}"
            )
        }.getOrDefault("")
        if (raw.isBlank()) return null
        val saved = runCatching {
            val parsed = urlaubJson.parseToJsonElement(raw).jsonObject["anspruch"].toString()
            urlaubJson.decodeFromString<Urlaubsanspruch>(parsed)
        }.getOrNull()
        if (saved != null) {
            triggerUrlaubRefresh(userId)
            val userPart = userId.trim().ifBlank { "~" }
            GlobalManager.markJsonChanged("urlaub", "user/$userPart/kontingente/$year.json")
            GlobalManager.markJsonChanged("zeit", "user/$userPart/kontingente/$year.json")
        }
        return saved
    }

    suspend fun saveReclassification(
        userId: String,
        year: Int,
        direction: String,
        tage: Double,
        stunden: Double
    ): Boolean {
        if (userId.isBlank()) return false
        val body = buildJsonObject {
            put("userId", JsonPrimitive(userId))
            put("jahr", JsonPrimitive(year))
            put("direction", JsonPrimitive(direction))
            put("tage", JsonPrimitive(tage))
            put("stunden", JsonPrimitive(stunden))
        }.toString()
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "personalkontingent/umwidmung-json/${apiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        val saved = raw.contains("\"ok\":true") || raw.contains("\"ok\": true")
        if (saved) {
            triggerUrlaubRefresh(userId)
            val userPart = userId.trim().ifBlank { "~" }
            GlobalManager.markJsonChanged("zeit", "user/$userPart/zeitkonto/journal/umwidmung.json")
            GlobalManager.markJsonChanged("urlaub", "user/$userPart/kontingente/$year.json")
        }
        return saved
    }

    suspend fun requestVacation(
        userId: String,
        datumVon: String,
        datumBis: String,
        abwesenheitsart: String = "Urlaub",
        beschreibung: String = "",
        status: Int = org.example.project.service.ZustandManager.Urlaub.BEANTRAGT
    ): Boolean {
        return requestVacationResult(userId, datumVon, datumBis, abwesenheitsart, beschreibung, status).first
    }

    suspend fun requestVacationResult(
        userId: String,
        datumVon: String,
        datumBis: String,
        abwesenheitsart: String = "Urlaub",
        beschreibung: String = "",
        status: Int = org.example.project.service.ZustandManager.Urlaub.BEANTRAGT
    ): Pair<Boolean, String> {
        if (userId.isBlank() || datumVon.isBlank() || datumBis.isBlank()) return false to "Bitte einen gültigen Zeitraum wählen."
        val body = buildJsonObject {
            put("userId", JsonPrimitive(userId))
            put("datumVon", JsonPrimitive(datumVon))
            put("datumBis", JsonPrimitive(datumBis))
            put("abwesenheitsart", JsonPrimitive(abwesenheitsart.ifBlank { "Urlaub" }))
            put("beschreibung", JsonPrimitive(beschreibung))
            put("zustand", JsonPrimitive(status))
        }.toString()
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/save-json/${apiPart(firmenId())}/${apiPart(body)}")
        }.getOrDefault("")
        val response = runCatching { urlaubJson.parseToJsonElement(raw).jsonObject }.getOrNull()
        val saved = response?.get("ok")?.jsonPrimitive?.content == "true"
        if (saved) triggerUrlaubRefresh(userId)
        val error = response?.get("error")?.jsonPrimitive?.content.orEmpty()
        val message = when (error) {
            "urlaub_zeitraum_belegt" -> {
                val existing = response?.get("urlaub") as? JsonObject
                val type = existing?.get("abwesenheitsart")?.jsonPrimitive?.content ?: "Urlaub/Abwesenheit"
                val from = existing?.get("datumVon")?.jsonPrimitive?.content.orEmpty()
                val to = existing?.get("datumBis")?.jsonPrimitive?.content.orEmpty()
                "In diesem Zeitraum ist bereits $type gebucht." +
                    if (from.isNotBlank() && to.isNotBlank()) " Bestehende Buchung: $from bis $to." else ""
            }
            "keine_arbeitstage" -> "Der Zeitraum enthält keine Arbeitstage laut Arbeitszeitprofil."
            "urlaub_anspruch_ueberschritten" -> "Der verfügbare Urlaubsanspruch reicht für diesen Zeitraum nicht aus."
            "zeitkonto_guthaben_zu_niedrig" -> "Die verfügbaren Überstunden reichen für diesen Zeitraum nicht aus."
            "tagesarbeitszeit_fehlt" -> "Im Arbeitszeitprofil fehlt die Tagesarbeitszeit."
            "ungueltige_anfrage" -> "Bitte einen gültigen Zeitraum wählen."
            else -> if (error.isNotBlank()) "Abwesenheit konnte nicht beantragt werden (Fehler: $error)."
                else "Keine gültige Serverantwort. Bitte die Verbindung prüfen und die Anträge vor einem erneuten Versuch aktualisieren."
        }
        return saved to message
    }

    suspend fun replaceDayWithAbsence(
        userId: String,
        datumVon: String,
        datumBis: String,
        abwesenheitsart: String,
        status: Int = org.example.project.service.ZustandManager.Urlaub.GENEHMIGT
    ): Boolean {
        if (userId.isBlank() || datumVon.isBlank() || datumBis.isBlank()) return false
        val body = buildJsonObject {
            put("userId", JsonPrimitive(userId))
            put("datumVon", JsonPrimitive(datumVon))
            put("datumBis", JsonPrimitive(datumBis))
            put("abwesenheitsart", JsonPrimitive(abwesenheitsart.ifBlank { "Urlaub" }))
            put("zustand", JsonPrimitive(status))
        }.toString()
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/replace-day-json/${apiPart(firmenId())}/${apiPart(body)}")
        }.getOrDefault("")
        val saved = raw.contains("\"ok\":true") || raw.contains("\"ok\": true")
        if (saved) triggerUrlaubRefresh(userId)
        return saved
    }

    suspend fun updateStatus(id: String, status: Int): Boolean {
        if (id.isBlank()) return false
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/status/${apiPart(firmenId())}/${apiPart(id)}/${apiPart(status.toString())}"
            )
        }.getOrDefault("")
        val saved = raw.contains("\"ok\":true") || raw.contains("\"ok\": true")
        if (saved) triggerUrlaubRefresh()
        return saved
    }
}
